<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { marked } from 'marked';
import { useCookieConsent } from '@/composables/useCookieConsent';
import { i18n, t } from '@/i18n';
import { LEGAL_COOKIE_CONSENT_MARKER, loadLegalMarkdown, type LegalPageId } from '@/lib/legal-docs';

const props = defineProps<{
  pageId: LegalPageId;
}>();

const { openPreferences } = useCookieConsent();
const articleRef = ref<HTMLElement | null>(null);
const source = ref('');
const loadError = ref('');
const teardownFns = ref<Array<() => void>>([]);

marked.setOptions({
  gfm: true,
  breaks: true,
});

function clearEnhancements() {
  for (const teardown of teardownFns.value) {
    try {
      teardown();
    } catch {
      // noop
    }
  }
  teardownFns.value = [];
}

function renderMarkdown(markdown: string) {
  const prepared = markdown.replace(
    LEGAL_COOKIE_CONSENT_MARKER,
    `<button type="button" data-legal-cookie-consent class="legal-cookie-consent-btn">${t('landingPages.legal.cookie.consent_button')}</button>`
  );
  return marked.parse(prepared.trim()) || '';
}

const html = computed(() => (source.value ? renderMarkdown(source.value) : ''));

function enhanceInteractiveElements() {
  clearEnhancements();
  const root = articleRef.value;
  if (!root) return;

  for (const button of root.querySelectorAll<HTMLButtonElement>('[data-legal-cookie-consent]')) {
    const onClick = () => openPreferences();
    button.addEventListener('click', onClick);
    teardownFns.value.push(() => button.removeEventListener('click', onClick));
  }
}

watch(
  () => [props.pageId, i18n.locale] as const,
  async ([pageId]) => {
    loadError.value = '';
    try {
      source.value = await loadLegalMarkdown(pageId);
    } catch (error) {
      loadError.value = error instanceof Error ? error.message : 'Failed to load document';
      source.value = '';
    }
  },
  { immediate: true }
);

watch(html, async () => {
  await nextTick();
  enhanceInteractiveElements();
});

onBeforeUnmount(() => {
  clearEnhancements();
});
</script>

<template>
  <div class="w-full flex flex-col gap-12 text-black/80">
    <p v-if="loadError" class="text-sm text-red-600">
      {{ loadError }}
    </p>

    <article v-else ref="articleRef" class="legal-prose" v-html="html" />
  </div>
</template>

<style scoped>
.legal-prose :deep(h2) {
  margin: 0 0 1rem;
  font-size: 1.5rem;
  line-height: 1.375;
  font-weight: 500;
  color: rgb(0 0 0 / 0.9);
}

.legal-prose :deep(h2:not(:first-child)) {
  margin-top: 3rem;
}

.legal-prose :deep(h3) {
  margin: 0.5rem 0 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  font-weight: 500;
  color: rgb(0 0 0 / 0.9);
}

.legal-prose :deep(p) {
  margin: 0 0 1rem;
  font-size: 1rem;
  line-height: 1.625;
  font-weight: 400;
  color: rgb(0 0 0 / 0.6);
}

.legal-prose :deep(p:last-child) {
  margin-bottom: 0;
}

.legal-prose :deep(ul) {
  margin: 0 0 1rem;
  padding-left: 1.25rem;
  list-style-type: disc;
}

.legal-prose :deep(li) {
  margin-bottom: 0.5rem;
  font-size: 1rem;
  line-height: 1.625;
  color: rgb(0 0 0 / 0.6);
}

.legal-prose :deep(strong) {
  font-weight: 600;
  color: rgb(0 0 0 / 0.9);
}

.legal-prose :deep(a),
.legal-prose :deep(.legal-cookie-consent-btn) {
  color: rgb(0 0 0 / 0.9);
  text-decoration: underline;
  transition: opacity 0.15s ease;
}

.legal-prose :deep(a:hover),
.legal-prose :deep(.legal-cookie-consent-btn:hover) {
  opacity: 0.7;
}

.legal-prose :deep(.legal-cookie-consent-btn) {
  font: inherit;
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
}
</style>
